/*
-----------------------
How to use this plugin:
-----------------------

Once installed in main.js, the component ToastsContainer must be instantiated at the top level of the HTML tree (i.e.: App.vue)
The plugin must be injected in each component it'll be used (const toast = inject('toast')). After that, it can be called using
toast followed by on of the available styles:

  .info() => shows a toast with an info related icon and emoji.
  .success() => shows a toast with a success related icon and emoji.
  .warning() => shows a toast with a warning related icon and emoji.
  .error() => shows a toast with an error related icon and emoji.

The toast accepts as parameters a message and actions:

  Message: A string

  Actions: An object with two attributes (primary and secondary). Each attribute should contain an object with two attributes:
    - text: A String. This is the text contained in the toast action button.
    - action: A function related to the action that is required to be done once the user clicks at the button.

The maximum number of toasts at the same time is 5.

EXAMPLE: toast.info('Hey, I\'m a toast', { primary: { text: 'This is an action button', action: () => route.push('/') } })

-----------------------
*/
import { reactive, nextTick } from 'vue'

import { hash } from '@dogfy/lib/utils'

function getToastPlugin ({ maxToasts }) {
  const toasts = reactive(new Map())

  async function toast (data) {
    const { type, message, actions } = data
    const toastId = await hash(`${type}-${message}`, { type: 'SHA-1' })

    // Additional check to avoid duplicated toasts.
    if (toasts.has(toastId)) {
      toasts.delete(toastId)
      await nextTick() // Required to allow remove animation.
    } else if (toasts.size === maxToasts) {
      const firstKey = toasts.keys().next().value
      toasts.delete(firstKey)
    }

    const toast = {
      type,
      message,
      actions,
    }

    toasts.set(toastId, toast)
  }

  // Short hand usages.
  toast.info = (message, actions) => toast({ type: 'info', message, actions })
  toast.success = (message, actions) => toast({ type: 'success', message, actions })
  toast.warning = (message, actions) => toast({ type: 'warning', message, actions })
  toast.error = (message, actions) => toast({ type: 'error', message, actions })

  toast.removeToast = (toastId) => toasts.delete(toastId)
  toast.removeAllToasts = () => toasts.clear()

  toast.toasts = toasts

  return toast
}

export default {
  install (app, options = { maxToasts: 5 }) {
    const toast = getToastPlugin(options)
    app.provide('toast', toast)
  },
}
